import { config } from '../config';
import { apiRequest, apiUpload } from './client';

const base = config.shopApiUrl;

function withShop(shopId, options = {}) {
  return { ...options, shopId, requireShopId: true };
}

function queryString(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  });
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

export const shopApi = {
  // Shop profile & domain
  getMe: (token) => apiRequest(base, '/api/v1/me', { token }),
  listMyShops: (token) => apiRequest(base, '/api/v1/me/shops', { token }),
  createMyShop: (token, payload) =>
    apiRequest(base, '/api/v1/me/shops', { token, method: 'POST', body: payload }),
  getShop: (token, shopId) => apiRequest(base, '/api/v1/shop', withShop(shopId, { token })),
  updateShop: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/shop', withShop(shopId, { token, method: 'PATCH', body: payload })),
  getDomainSettings: (token, shopId) =>
    apiRequest(base, '/api/v1/shop/domain', withShop(shopId, { token })),
  updateDomainSettings: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/shop/domain', withShop(shopId, { token, method: 'PUT', body: payload })),

  // Members
  listMembers: (token, shopId) =>
    apiRequest(base, '/api/v1/members', withShop(shopId, { token })),
  addMember: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/members', withShop(shopId, { token, method: 'POST', body: payload })),
  updateMember: (token, shopId, userId, payload) =>
    apiRequest(base, `/api/v1/members/${userId}`, withShop(shopId, { token, method: 'PATCH', body: payload })),
  removeMember: (token, shopId, userId) =>
    apiRequest(base, `/api/v1/members/${userId}`, withShop(shopId, { token, method: 'DELETE' })),

  // Products & categories
  listProducts: (token, shopId) =>
    apiRequest(base, '/api/v1/products', withShop(shopId, { token })),
  getProduct: (token, shopId, productId) =>
    apiRequest(base, `/api/v1/products/${productId}`, withShop(shopId, { token })),
  createProduct: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/products', withShop(shopId, { token, method: 'POST', body: payload })),
  updateProduct: (token, shopId, productId, payload) =>
    apiRequest(base, `/api/v1/products/${productId}`, withShop(shopId, { token, method: 'PATCH', body: payload })),
  createVariant: (token, shopId, productId, payload) =>
    apiRequest(base, `/api/v1/products/${productId}/variants`, withShop(shopId, { token, method: 'POST', body: payload })),
  updateVariant: (token, shopId, productId, variantId, payload) =>
    apiRequest(base, `/api/v1/products/${productId}/variants/${variantId}`, withShop(shopId, { token, method: 'PATCH', body: payload })),
  deleteVariant: (token, shopId, productId, variantId) =>
    apiRequest(base, `/api/v1/products/${productId}/variants/${variantId}`, withShop(shopId, { token, method: 'DELETE' })),
  uploadProductImage: (token, shopId, productId, file, altText = '') =>
    apiUpload(base, `/api/v1/products/${productId}/images`, withShop(shopId, { token, file, fields: { alt_text: altText } })),
  removeProductImage: (token, shopId, productId, imageId) =>
    apiRequest(base, `/api/v1/products/${productId}/images/${imageId}`, withShop(shopId, { token, method: 'DELETE' })),
  uploadVariantImage: (token, shopId, productId, variantId, file, altText = '') =>
    apiUpload(base, `/api/v1/products/${productId}/variants/${variantId}/image`, withShop(shopId, { token, file, fields: { alt_text: altText } })),
  removeVariantImage: (token, shopId, productId, variantId) =>
    apiRequest(base, `/api/v1/products/${productId}/variants/${variantId}/image`, withShop(shopId, { token, method: 'DELETE' })),
  listCategories: (token, shopId) =>
    apiRequest(base, '/api/v1/products/categories', withShop(shopId, { token })),
  createCategory: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/products/categories', withShop(shopId, { token, method: 'POST', body: payload })),
  listCollections: (token, shopId) =>
    apiRequest(base, '/api/v1/products/collections', withShop(shopId, { token })),
  createCollection: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/products/collections', withShop(shopId, { token, method: 'POST', body: payload })),
  listTags: (token, shopId) =>
    apiRequest(base, '/api/v1/products/tags', withShop(shopId, { token })),
  createTag: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/products/tags', withShop(shopId, { token, method: 'POST', body: payload })),
  listProductTypes: (token, shopId) =>
    apiRequest(base, '/api/v1/products/product-types', withShop(shopId, { token })),

  // Orders
  listOrders: (token, shopId) =>
    apiRequest(base, '/api/v1/orders', withShop(shopId, { token })),
  getOrder: (token, shopId, orderId) =>
    apiRequest(base, `/api/v1/orders/${orderId}`, withShop(shopId, { token })),
  createOrder: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/orders', withShop(shopId, { token, method: 'POST', body: payload })),
  updateOrder: (token, shopId, orderId, payload) =>
    apiRequest(base, `/api/v1/orders/${orderId}`, withShop(shopId, { token, method: 'PATCH', body: payload })),

  // Customers
  listCustomers: (token, shopId) =>
    apiRequest(base, '/api/v1/customers', withShop(shopId, { token })),
  getCustomer: (token, shopId, customerId) =>
    apiRequest(base, `/api/v1/customers/${customerId}`, withShop(shopId, { token })),
  createCustomer: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/customers', withShop(shopId, { token, method: 'POST', body: payload })),
  updateCustomer: (token, shopId, customerId, payload) =>
    apiRequest(base, `/api/v1/customers/${customerId}`, withShop(shopId, { token, method: 'PATCH', body: payload })),

  // Customer addresses
  listCustomerAddresses: (token, shopId, customerId) =>
    apiRequest(base, `/api/v1/customers/${customerId}/addresses`, withShop(shopId, { token })),
  createCustomerAddress: (token, shopId, customerId, payload) =>
    apiRequest(base, `/api/v1/customers/${customerId}/addresses`, withShop(shopId, { token, method: 'POST', body: payload })),
  updateCustomerAddress: (token, shopId, customerId, addressId, payload) =>
    apiRequest(base, `/api/v1/customers/${customerId}/addresses/${addressId}`, withShop(shopId, { token, method: 'PATCH', body: payload })),
  deleteCustomerAddress: (token, shopId, customerId, addressId) =>
    apiRequest(base, `/api/v1/customers/${customerId}/addresses/${addressId}`, withShop(shopId, { token, method: 'DELETE' })),

  // Inventory
  listWarehouses: (token, shopId) =>
    apiRequest(base, '/api/v1/inventory/warehouses', withShop(shopId, { token })),
  createWarehouse: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/inventory/warehouses', withShop(shopId, { token, method: 'POST', body: payload })),
  updateWarehouse: (token, shopId, warehouseId, payload) =>
    apiRequest(base, `/api/v1/inventory/warehouses/${warehouseId}`, withShop(shopId, { token, method: 'PATCH', body: payload })),
  listStock: (token, shopId, params = {}) =>
    apiRequest(base, `/api/v1/inventory/stock${queryString(params)}`, withShop(shopId, { token })),
  adjustStock: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/inventory/stock/adjust', withShop(shopId, { token, method: 'POST', body: payload })),
  receiveStock: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/inventory/stock/receive', withShop(shopId, { token, method: 'POST', body: payload })),
  listReservations: (token, shopId, params = {}) =>
    apiRequest(base, `/api/v1/inventory/reservations${queryString(params)}`, withShop(shopId, { token })),
  listLedger: (token, shopId, params = {}) =>
    apiRequest(base, `/api/v1/inventory/ledger${queryString(params)}`, withShop(shopId, { token })),
  listTransfers: (token, shopId, params = {}) =>
    apiRequest(base, `/api/v1/inventory/transfers${queryString(params)}`, withShop(shopId, { token })),
  createTransfer: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/inventory/transfers', withShop(shopId, { token, method: 'POST', body: payload })),
  dispatchTransfer: (token, shopId, transferId) =>
    apiRequest(base, `/api/v1/inventory/transfers/${transferId}/dispatch`, withShop(shopId, { token, method: 'POST' })),
  receiveTransfer: (token, shopId, transferId) =>
    apiRequest(base, `/api/v1/inventory/transfers/${transferId}/receive`, withShop(shopId, { token, method: 'POST' })),
  cancelTransfer: (token, shopId, transferId) =>
    apiRequest(base, `/api/v1/inventory/transfers/${transferId}/cancel`, withShop(shopId, { token, method: 'POST' })),

  // Billing
  createInvoice: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/billing/invoices', withShop(shopId, { token, method: 'POST', body: payload })),
  getInvoice: (token, shopId, invoiceId) =>
    apiRequest(base, `/api/v1/billing/invoices/${invoiceId}`, withShop(shopId, { token })),
  createPayment: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/billing/payments', withShop(shopId, { token, method: 'POST', body: payload })),

  // Currency
  listExchangeRates: (token, shopId) =>
    apiRequest(base, '/api/v1/currency/rates', withShop(shopId, { token })),
  createExchangeRate: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/currency/rates', withShop(shopId, { token, method: 'POST', body: payload })),
  updateExchangeRate: (token, shopId, rateId, payload) =>
    apiRequest(base, `/api/v1/currency/rates/${rateId}`, withShop(shopId, { token, method: 'PATCH', body: payload })),
  deleteExchangeRate: (token, shopId, rateId) =>
    apiRequest(base, `/api/v1/currency/rates/${rateId}`, withShop(shopId, { token, method: 'DELETE' })),
  convertCurrency: (token, shopId, payload) =>
    apiRequest(base, '/api/v1/currency/convert', withShop(shopId, { token, method: 'POST', body: payload })),
};

export const tenantApi = shopApi;
